// Транспорт и хуки раздела «Задания процессов» (зеркало bizproc.* Bitrix). ТОЛЬКО ЧТЕНИЕ.
//
// Записи здесь нет и в ближайшее время быть не может, и это не «пока не сделали»:
// bizproc.task.complete выполняет ТОЛЬКО СВОЁ задание — Bitrix ищет его среди заданий
// автора REST-запроса. CRM ходит служебным вебхуком (пользователь 1), поэтому чужое
// задание она не найдёт вовсе, а своё закрыла бы от имени живого человека. Чтобы
// закрывать задания из CRM, нужна персональная OAuth-привязка каждого сотрудника —
// отдельный шаг и отдельное решение владельца. До тех пор кнопка ведёт в Bitrix.

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'

const БАЗА = '/api/bp'

/** Тот же разбор ответа, что в api/client.ts, api/p73.ts и api/p73x.ts. */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    // 400 от ядра — отказ по существу фильтра. Пустая таблица читалась бы как
    // «ничего не висит», а это ровно та ложь, ради которой раздел и заводился.
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

export function строкаЗапроса(ф: Record<string, unknown>): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(ф)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    p.set(k, v === true ? '1' : String(v))
  }
  const s = p.toString()
  return s ? '?' + s : ''
}

const ОБЩЕЕ = { staleTime: 60_000, retry: 1 } as const

// ─────────── формы ответов ядра (см. bp_api.py) ───────────

export interface Список<T> {
  rows: T[]
  total: number
  next: number | null
  limit: number
  start: number
  note?: string
}

export interface ЗаданиеСтрока {
  bitrix_id: number
  /** Ключ строки — пара «задание-человек»: bitrix_id не уникален. Собран ядром. */
  row_key: string
  /** Скольких людей ждёт это же задание. Бывает до 70 — массовое ознакомление. */
  waiting_people: number
  name: string | null
  activity: string | null
  user_id: number | null
  user_name: string | null
  /** null = такой учётки нет в зеркале сотрудников; false = человек отключён. */
  user_active: boolean | null
  template_id: number | null
  template_name: string | null
  workflow_id: string | null
  workflow_state: string | null
  module_id: string | null
  doc_kind: string | null
  doc_type_id: number | null
  doc_item_id: number | null
  deal_id: number | null
  deal_title: string | null
  deal_stage: string | null
  document_name: string | null
  document_url: string | null
  workflow_started: string | null
  modified: string | null
  overdue_date: string | null
  fields_count: number | null
  has_file_field: boolean | null
  /** Обязательное файловое поле: такое задание через REST не закрыть ничем. */
  req_file_field: boolean | null
  days_waiting: number | null
  /** Реальный срок из настроек задания. Bitrix проставляет его редко — см. оговорки. */
  overdue_bitrix: boolean | null
}

export interface ЧеловекСтрока {
  user_id: number | null
  user_name: string | null
  user_active: boolean | null
  tasks: number
  approve: number
  review: number
  fill: number
  old: number
  overdue: number
  deals: number
  oldest_started: string | null
  oldest_days: number | null
  median_days: number | null
}

export interface ЭкземплярСтрока {
  workflow_id: string
  template_id: number | null
  template_name: string | null
  is_dead_name: boolean | null
  module_id: string | null
  doc_kind: string | null
  doc_type_id: number | null
  doc_item_id: number | null
  deal_id: number | null
  deal_title: string | null
  document_id: string | null
  started: string | null
  started_by: number | null
  modified: string | null
  days_running: number | null
  live_tasks: number
  /** Состояние из заданий процесса. У процесса без заданий его нет — и это честно пусто. */
  state_guess: string | null
}

export interface ШаблонСтрока {
  bitrix_id: number
  name: string | null
  module_id: string | null
  doc_type: string | null
  auto_execute: number | null
  is_dead_name: boolean | null
  tasks_all: number | null
  tasks_live: number | null
  instances_live: number | null
  stat_at: string | null
  never_ran: boolean | null
  dead_but_alive: boolean | null
  /** false = робот или триггер воронки: bizproc.workflow.template.list его не отдаёт. */
  listed: boolean | null
}

export interface Справочники {
  freshness: { entity: string; last_sync_at: string | null; age_min: number | null }[]
  counts: {
    tasks_live: number | null
    tasks_total: number | null
    people: number | null
    deals: number | null
    history_from: string | null
    instances: number | null
    idle: number | null
    /** Строк 6 989, а разных заданий 6 144 (на 28.07.2026): одно задание может ждать
     *  нескольких. */
    tasks_live_distinct: number | null
    hidden_templates: number | null
    hidden_instances: number | null
    hidden_tasks: number | null
  }
  activities: { activity: string | null; n: number }[]
  doc_kinds: { doc_kind: string | null; doc_type_id: number | null; n: number }[]
  templates: { bitrix_id: number; name: string | null; doc_type: string | null;
               is_dead_name: boolean | null; live: number }[]
  people: { id: number | null; name: string | null; active: boolean | null; live: number }[]
  old_days: number
  caveats: string[]
  activity_names: Record<string, string>
  doc_kind_names: Record<string, string>
  auto_execute_names: Record<string, string>
}

export function useСправочники() {
  return useQuery<Справочники>({
    queryKey: ['bp', 'refs'],
    queryFn: () => запрос('/refs'),
    ...ОБЩЕЕ,
  })
}

/**
 * Живые задания процессов. Фильтры уходят в ядро как есть — среди них с 28.07.2026 есть
 * `company` (все задания по сделкам клиента) и `deal_bx` (списком номеров сделок).
 *
 * `включён` — для экранов, где блок закрыт правами: раздел 'bp-tasks' ролевой, и у роли без
 * него запрос вернул бы 403 на каждое открытие. По умолчанию true — прежние вызовы
 * (кабинет руководителя, раздел заданий) не меняются.
 *
 * Сводкой по людям (`/bp/by-user`) то же самое спросить НЕЛЬЗЯ: она агрегат по всем живым
 * заданиям, и на `?company=` ядро отвечает 400 с объяснением. Такого вызова здесь нет.
 */
export function useЗадания(ф: Record<string, unknown>, включён = true) {
  return useQuery<Список<ЗаданиеСтрока> & { state: string; old_days: number }>({
    queryKey: ['bp', 'tasks', ф],
    queryFn: () => запрос('/tasks' + строкаЗапроса(ф)),
    enabled: включён,
    ...ОБЩЕЕ,
  })
}

export function useПоЛюдям(ф: Record<string, unknown>) {
  return useQuery<Список<ЧеловекСтрока> & {
    totals: { tasks: number; people: number; left_people: number; left_tasks: number
              unknown_people: number }
    old_days: number
  }>({
    queryKey: ['bp', 'by-user', ф],
    queryFn: () => запрос('/by-user' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

// ─────────── сводка «висит на уволенных» (GET /bp/left) ───────────

export interface УволСтрока {
  user_id: number | null
  user_name: string | null
  tasks: number
  deals: number
  oldest_started: string | null
  oldest_days: number | null
  open_deals: number
  /** Сумма ОТКРЫТЫХ рублёвых сделок человека. Валюты не складываются — см. note ядра. */
  open_rub: number | null
  /** Открытые сделки не в рублях: их сумма не показана, но о них сказано. */
  open_other: number
}

export interface УволДеньги {
  closed: boolean | null
  currency: string | null
  deals: number
  amount: number | null
}

export interface УволОтвет {
  rows: УволСтрока[]
  total: number
  totals: {
    waits: number
    tasks: number
    people: number
    waits_with_deal: number
    deals: number
    oldest_started: string | null
    oldest_days: number | null
  }
  money: УволДеньги[]
  note: string
}

export function useНаУволенных() {
  return useQuery<УволОтвет>({
    queryKey: ['bp', 'left'],
    queryFn: () => запрос('/left'),
    ...ОБЩЕЕ,
  })
}

export function useЭкземпляры(ф: Record<string, unknown>) {
  return useQuery<Список<ЭкземплярСтрока>>({
    queryKey: ['bp', 'instances', ф],
    queryFn: () => запрос('/instances' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

export function useШаблоны(ф: Record<string, unknown>) {
  return useQuery<Список<ШаблонСтрока> & { kind: string }>({
    queryKey: ['bp', 'templates', ф],
    queryFn: () => запрос('/templates' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}
